import React, {ReactNode, useEffect, useState} from 'react';
import FormLayout from '@/Layouts/FormLayout';
import PageContainer from '@/Components/PageContainer';
import {FormBuilderForm, FormBuilderPage, FormViewerRouteParams, Office, Team, User} from '@/types';
import Block from '@/Components/FormViewer/Block';
import {Head, Link, router, useForm, usePage} from '@inertiajs/react';
import route from 'ziggy-js';
import ErrorAlert from '@/Components/TailwindUI/Alert/WithList';
import axios from 'axios';
import DebugDiv from '@/Components/FormViewer/DebugDiv';
import urlGenerator from '@/lib/urlGenerator';
import FormViewerPagination from '@/Components/FormViewer/FormViewerPagination';
import LoadingIcon from '@/Components/LoadingIcon';
import ReactMarkdown from 'react-markdown';
import {useRecaptcha} from '@/Hooks/useRecaptcha';
import formRequiresUpgrade from '@/lib/formRequiresUpgrade';
import AlertWithAction from '@/Components/AlertWithAction';
import Input from "@/Components/TailwindUI/Input";
import Account from "@/Components/Icons/Account";
import Dropdown from "@/Components/Dropdown";
import {Page, UserDetail} from "@/types/model";
import CreateAccount from "@/Components/FormViewer/CreateAccount";
import Logout from "@/Components/Icons/Logout";
import EmptyDocument from "@/Components/Icons/EmptyDocument";

type FormValue = string | number | boolean | null | string[] | File[]
type OptionalOffice = Office | null
let syncToServerTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

type GrecaptchaProps = {
    ready: (cb: () => void) => void,
    execute: (key: string, options: { action: string }) => Promise<string>
}
declare const grecaptcha: GrecaptchaProps;

interface Props {
    form: FormBuilderForm,
    team: Team,
    page: FormBuilderPage
    formValues: { [name: string]: FormValue },
    hiddenPageFormValues: { [name: string]: FormValue },
    pageCount: number,
    office: OptionalOffice,
    latestPage: number,
    hiddenUrlParams: { string: string } | undefined,
    pages: FormBuilderPage[],
    relevantPages: FormBuilderPage[],
    offices: { [id: string]: string },
    questionIdsInHiddenPageLogic: string[],
    userDetails: UserDetail[],
    existingEntryId: string | null,
    propertyAddress: string | null,
    journeyId: string | null
}

function syncToServer(setIsSyncing, post, routeParams, key: any, value: any) {
    clearTimeout(syncToServerTimeout);
    setIsSyncing(false);

    syncToServerTimeout = setTimeout(() => {
        setIsSyncing(true);
        routeParams['key'] = key;
        router.post(route('form-viewer.sync-answer', routeParams),
            {
                key: key,
                value: value,
            },
            {
                onSuccess: () => {
                },
                onError: (errors) => {
                    console.log('There was an error syncing to the server', errors);
                },
                onFinish: () => {
                    setIsSyncing(false);
                },
            },
        );
    }, 250);
    // post(route('form-viewer.sync-answer', routeParams), {
    //     preserveScroll: true,
    //     onSuccess: () => {
    //     },
    //     onError: () => {
    //     }
    // })
}

// Helper function for storing form data locally to save
function setFormAndRemember(setIsSyncing, post, page, hiddenUrlParams, office, questionIdsInHiddenPageLogic, setData, /*formState,*/ data/* setFormState*/) {
    return (key, value) => {
        const routeParams: FormViewerRouteParams = urlGenerator(page.form_id, page.number, hiddenUrlParams);
        if (office) {
            routeParams['office'] = office.id;
        }
        // We only want to sync to server if the question is in a page hidden question logic, as it allows us to update the hidden pages
        if (questionIdsInHiddenPageLogic.includes(key)) {
            syncToServer(setIsSyncing, post, routeParams, key, value);
        }
        // setFormState({...data, [key]: value})
        return setData({...data, [key]: value});
    };
}

export default function Show({
                                 form,
                                 team,
                                 page,
                                 formValues,
                                 hiddenPageFormValues,
                                 pageCount,
                                 office,
                                 latestPage,
                                 hiddenUrlParams,
                                 pages,
                                 relevantPages,
                                 settings,
                                 offices,
                                 questionIdsInHiddenPageLogic,
                                 userDetails,
                                 existingEntryId,
                                 propertyAddress,
                                 journeyId
                             }: Props) {
    const {data, setData, post, processing, errors, clearErrors, transform} = useForm('FormViewer_Show', formValues);
    const {auth, googleRecaptchaKey} = usePage().props as unknown as {
        auth: { user: User | null },
        googleRecaptchaKey: string
    };
    const [showDev, setShowDev] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    useRecaptcha(googleRecaptchaKey);
    useEffect(() => {
        console.log('user details', userDetails)
        // This is a little tweak for an issue with form submission in inertia js
        // After a successful submission and redirect to the next page
        // Intertia was keeping the 'data' variable with the old page data
        // This caused the hide logic to not work as formValues were missing for the new page
        // So now, we check if formValues has changed, and update the data state when it does
        setData(formValues);
        document.body.classList.add('bg-white')
    }, [page.number]);

// Here we can transform the data before it is sent to the server
    transform((data) => {
        Object.keys(data).forEach((key) => {
            // Sometimes React Dropzone tries to pass a [null] on empty uploads, PHP thinks that counts as a validated file so we remove it.
            /* @ts-expect-error - this works even though eslint says otherwise */
            if (data[key] === [null] || (Array.isArray(data[key]) && data[key]?.length == 0)) {
                // /* @ts-expect-error - this works even though eslint says otherwise */
                data[key] = [];
            }
        });
        return data;
    });

    function submit(e) {
        e.preventDefault();
        clearErrors();

        const routeParams: FormViewerRouteParams = urlGenerator(page.form_id, page.number, hiddenUrlParams);
        if (form.has_bot_protection) {
            grecaptcha.ready(function () {
                grecaptcha.execute(googleRecaptchaKey, {action: 'submit'}).then(function (token: string) {
                    if (token) {
                        routeParams['g-recaptcha-response'] = token;
                    }
                    sendFormToServer(routeParams);
                });
            });
        } else {
            sendFormToServer(routeParams);
        }
    }

    function sendFormToServer(routeParams: FormViewerRouteParams) {
        if (office) {
            routeParams['office'] = office.id;
        }
        post(route('form-viewer.update', routeParams), {
            preserveScroll: false,
            onSuccess: () => {
            },
            onError: (errors) => {
                console.log('error', errors);
            },
        });
    }


    const a = (data) => {
        return Object
            .keys(data)
            .map(value => `${value}=${encodeURIComponent(data[value])}`)
            .join('&');
    };
    return (
        <form
            onSubmit={submit}
            className={' flex flex-col bg-white min-h-full h-full'}
        >
            <Head title={form.name}/>
            <div className='pt-2 p pb-4 px-4 md:hidden'>
                <MaxWidthDiv>
                    <FormProgressBar currentPage={relevantPages.findIndex((p: FormBuilderPage) => {
                        return p.number === page.number
                    }) + 1} totalPages={relevantPages.length} settings={settings}
                                     user={auth.user}/>
                </MaxWidthDiv>
            </div>
            <div className={' '}>
                <div className={'px-4'}>
                    <MaxWidthDiv>
                        <div className='pt-2 pb-4 hidden md:block'>
                            <MaxWidthDiv>
                                <FormProgressBar currentPage={relevantPages.findIndex((p: FormBuilderPage) => {
                                    return p.number === page.number
                                }) + 1} totalPages={relevantPages.length} settings={settings}
                                                 user={auth.user}/>
                            </MaxWidthDiv>
                        </div>
                        <div className="grow">
                            {form.has_logo && (
                                <img src={form.logo} alt="Company Logo" className="max-h-[100px] mx-auto mb-4"/>)}
                            <div className="relative">
                                {isSyncing && <LoadingIcon className={'absolute top-0 bottom-0 right-0 w-6 h-6'}/>}
                            </div>
                        </div>
                        <div className={'grow overflow-y-auto px-0.5'}>
                            {formRequiresUpgrade(form, team) &&
                                <AlertWithAction style="info" showArrow={false} className="mb-4">
                                    <div><strong>Preview only</strong><br/>This form is
                                        for preview only. Please upgrade your package to use it.
                                    </div>
                                </AlertWithAction>}

                            {displayErrors(errors)}
                            <fieldset className="divide-solid divide-gray-100 divide-y divide-y-6"
                                      disabled={processing || isSyncing}>
                                {page.blocks.map((block) => (
                                    <Block data={data}
                                           key={block.id}
                                           setData={setFormAndRemember(setIsSyncing, post, page, hiddenUrlParams, office, questionIdsInHiddenPageLogic, setData, data)}
                                           block={block}
                                           deleteFiles={deleteFiles}
                                           errors={errors}
                                           officeId={office?.id}
                                           showDev={showDev}
                                           settings={settings}
                                           offices={offices}
                                           page={page}
                                           existingEntryId={existingEntryId}
                                           propertyAddress={propertyAddress}
                                           journeyId={journeyId}
                                           form={form}
                                    />
                                ))}
                            </fieldset>
                        </div>
                    </MaxWidthDiv>
                </div>
            </div>
            <div>
                <FormViewerPagination
                    pageCount={pageCount}
                    page={page}
                    processing={processing}
                    data={{...data, ...hiddenPageFormValues}}
                    submit={submit}
                    office={office}
                    latestPage={latestPage}
                    hiddenUrlParams={hiddenUrlParams}
                    pages={relevantPages}
                    settings={settings}
                />
            </div>
            <div className="block mt-4 mx-4 md:px-4 text-gray-400 md:mx-auto"><ReactMarkdown
                className="text-sm text-gray-400 mx-auto max-w-2xl">{form.footer_text}</ReactMarkdown></div>
        </form>
    );
}


function displayErrors(errors) {
    const entries = Object.entries(errors);
    if (entries.length === 0) {
        return <></>;

    }
    return (<div>
        {<ErrorAlert className="mb-4">{Object.entries(errors).map(([key, value]) => {
            return <li key={key}>{value}</li>;
        })}
        </ErrorAlert>}
    </div>);
}

function deleteFiles(question, data, setData, officeId) {
    axios.delete(route('form-viewer.files.delete', {form: question.form_id, question: question.id, office: officeId}))
        .then(() => {
            setData(question.id, [null]);
        }).catch((e) => {
    });

}

function FormProgressBar({currentPage, totalPages, settings, user}): ReactNode {
    console.log('c', currentPage, totalPages)
    let percentage = ((currentPage / totalPages) * 100).toString() + '%'
    if (totalPages == 1) {
        return user ? (<div className={'flex justify-end '}>{dropdown()}</div>) : (<></>)
    }

    function dropdown() {
        return (
            <div className={''}>
                <Dropdown>
                    <Dropdown.Trigger><Account className={'cursor-pointer'}/></Dropdown.Trigger>
                    <Dropdown.Content>
                        <Dropdown.Link href={route('dashboard')} target={'_blank'} className={' stroke-gray-700 group flex gap-x-3 items-center'}>
                            <div className={'w-5 flex justify-center'}>
                                <EmptyDocument/>
                            </div>
                            Your Account</Dropdown.Link>
                        <hr className={'bg-gray-100 border-gray-100'}/>
                        <Dropdown.Link
                            href={'logout'}
                            method={'post'}
                            className={'text-red-400 hover:bg-gray-50 hover:text-red-500 hover:stroke-red-500 group flex gap-x-3 rounded-md p-2 text-sm font-normal leading-6 stroke-red-600 items-center'}
                        >
                            <div className={'w-5 flex justify-center'}>
                                <Logout/>
                            </div>
                            Logout
                        </Dropdown.Link>
                    </Dropdown.Content>
                </Dropdown>

            </div>
        )
    }

    return <div className='w-full flex items-center space-x-2'>
        {totalPages > 1 && <div className={'font-semibold text-xs'}>{currentPage}/{totalPages}</div>}
        <div className={'grow bg-gray-100 w-full h-2'}>
            <div className={`bg-[${settings.primaryColour}] h-2`}
                 style={{"width": percentage, maxWidth: percentage}}></div>
        </div>
        {user && dropdown()}
    </div>
}


function MaxWidthDiv({children}) {
    return (<div className={'max-w-2xl mx-auto'}>{children}</div>)
}
